import { useOutletContext, useNavigate } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import type { Project } from '@shared/types'
import { GCC_DEMO_MODE_BLOCKED_MESSAGE } from '@tn/shared'
import { useApi, useGenerationContext } from '../index'
import GccRenameModal from './GccRenameModal'
import { deleteGccProject, gccDesignDeleteMessage, planGccDesignDelete } from './deleteGccProject'
import { useGccRename } from './useGccRename'
import { canRenameGccProject, gccDisplayName } from './gccDesignName'
import { resolveSessionIdentity } from '../utils/sessionIdentity'

function DemoBanner({ onLoad }: { onLoad: () => void }) {
  const api = useApi()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleLoad = async () => {
    if (!api.seedGccDemo) return
    setLoading(true)
    setError('')
    try {
      await api.setDemoActive(true)
      await api.seedGccDemo()
      onLoad()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load demo projects')
    } finally {
      setLoading(false)
    }
  }

  if (!api.seedGccDemo) return null

  return (
    <div className="mt-4 rounded-xl border border-purple-100 bg-purple-50 px-5 py-4 flex items-center justify-between gap-4 max-w-md mx-auto">
      <div>
        <p className="text-xs font-semibold text-purple-800">Try the demo</p>
        <p className="text-xs text-purple-600 mt-0.5">Load pre-built GCC designs to explore the dashboard.</p>
        {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
      </div>
      <button
        onClick={() => void handleLoad()}
        disabled={loading}
        className="flex-shrink-0 text-xs font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-lg px-4 py-2 transition-colors disabled:opacity-50"
      >
        {loading ? 'Loading…' : 'Load demo projects'}
      </button>
    </div>
  )
}

function fmtM(usd: number): string {
  return `$${(usd / 1_000_000).toFixed(1)}M`
}

interface GccLayoutContext {
  refreshProjects: () => Promise<void>
  projects: Project[]
  isDemoActive?: boolean
}

interface CardMenuProps {
  /** The design's display name, so each card's menu button has its own accessible name. */
  designName: string
  /** Always visible for stuck/failed cards, hover-revealed otherwise. */
  alwaysVisible: boolean
  busy: boolean
  canRename: boolean
  onRename: () => void
  onDelete: (e: React.MouseEvent) => void
}

function CardMenu({ designName, alwaysVisible, busy, canRename, onRename, onDelete }: CardMenuProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={ref} className="absolute top-3 right-3 z-10">
      <button
        onClick={e => { e.stopPropagation(); setOpen(o => !o) }}
        disabled={busy}
        title="Design actions"
        aria-label={`Design actions for ${designName}`}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`w-6 h-6 flex items-center justify-center rounded-full text-sm transition-all
          ${alwaysVisible || open ? 'opacity-60 hover:opacity-100' : 'opacity-0 group-hover:opacity-60 hover:!opacity-100 focus:opacity-100'}
          text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30`}
      >
        {busy ? '…' : '⋮'}
      </button>
      {open && (
        <div role="menu" className="absolute right-0 mt-1 w-36 bg-white border border-gray-100 rounded-lg shadow-lg py-1 text-sm">
          {canRename && (
            <button
              role="menuitem"
              onClick={e => { e.stopPropagation(); setOpen(false); onRename() }}
              className="w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Rename
            </button>
          )}
          <button
            role="menuitem"
            onClick={e => { setOpen(false); onDelete(e) }}
            className="w-full text-left px-3 py-2 text-red-500 hover:bg-red-50 transition-colors"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  )
}

function DemoBadge() {
  return (
    <span className="text-[9px] font-bold uppercase tracking-widest bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded flex-shrink-0">
      Demo
    </span>
  )
}

export default function GccHome() {
  const { projects, refreshProjects, isDemoActive = false } = useOutletContext<GccLayoutContext>()
  const navigate = useNavigate()
  const api = useApi()
  const { activeJobs } = useGenerationContext()
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [sessionEmail, setSessionEmail] = useState('')
  const rename = useGccRename(() => refreshProjects())

  useEffect(() => {
    void resolveSessionIdentity(api).then(({ email }) => setSessionEmail(email))
  }, [api])

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDeletingId(id)
    try {
      // The design's TN Analyses and combined dashboards go with it (WI 2494).
      const plan = await planGccDesignDelete(api, id, activeJobs)
      if (!window.confirm(gccDesignDeleteMessage(plan))) return
      await deleteGccProject(api, plan)
      await refreshProjects()
    } catch {
      alert('Failed to delete design. Please try again.')
    } finally {
      setDeletingId(null)
    }
  }

  if (projects.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center">
        <div className="text-5xl mb-4">🏗️</div>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">No GCC designs yet</h1>
        <p className="text-sm text-gray-500 mb-6 max-w-xs">
          Create your first Global Capability Center workforce design.
        </p>
        <button
          onClick={() => navigate('/gcc/wizard')}
          disabled={isDemoActive}
          title={isDemoActive ? GCC_DEMO_MODE_BLOCKED_MESSAGE : undefined}
          className="px-6 py-3 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
        >
          Create First GCC Design
        </button>
        <DemoBanner onLoad={() => void refreshProjects()} />
      </div>
    )
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900">My GCC Designs</h1>
        <p className="text-sm text-gray-500 mt-1">{projects.length} design{projects.length !== 1 ? 's' : ''}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.map(p => {
          const gcc = p.gccData
          const color = p.branding?.primaryColor ?? '#A100FF'
          const isDeleting = deletingId === p.id
          // Summary fields are precomputed at save time and returned by list queries
          // even when the full gccData is stripped (CosmosDB LIST_FIELDS projection).
          const roleCount = gcc?.gccPopulation.endStateRoles.length ?? p.gccRoleCount
          const labourSaving = gcc?.combined.labourSavingUSD ?? p.gccLabourSavingUSD
          const isComplete = roleCount !== undefined && labourSaving !== undefined
          return (
            <div key={p.id} className="relative group">
              <button
                onClick={() => navigate(`/gcc/dashboard/${p.id}`)}
                disabled={isDeleting}
                className="w-full bg-white rounded-2xl overflow-hidden border border-gray-100 text-left hover:border-purple-200 hover:shadow-sm transition-all disabled:opacity-50"
              >
                <div className="h-1 w-full" style={{ background: color }} />
                <div className="flex items-center gap-2.5 mb-4 p-5 pb-0">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                    style={{ background: color }}
                  >
                    GCC
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <div className="text-sm font-semibold text-gray-900 truncate">{gccDisplayName(p)}</div>
                      {p.isDemo && <DemoBadge />}
                    </div>
                    <div className="text-xs text-gray-400">{p.industry}</div>
                  </div>
                </div>
                {isComplete ? (
                  <div className="grid grid-cols-2 gap-3 px-5 pb-5">
                    <div>
                      <div className="text-xl font-bold" style={{ color }}>
                        {roleCount}
                      </div>
                      <div className="text-xs text-gray-500">Global Roles</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold" style={{ color }}>
                        {fmtM(labourSaving)}
                      </div>
                      <div className="text-xs text-gray-500">Labour Saving</div>
                    </div>
                  </div>
                ) : p.gccError ? (
                  <div className="flex items-center gap-1.5 px-5 pb-5">
                    <span className="text-red-400 text-sm">⚠</span>
                    <span className="text-xs text-red-400">Generation failed — delete and retry</span>
                  </div>
                ) : (
                  <div className="text-xs text-gray-400 italic px-5 pb-5">Processing…</div>
                )}
              </button>
              <CardMenu
                designName={gccDisplayName(p)}
                alwaysVisible={!isComplete}
                busy={isDeleting}
                canRename={canRenameGccProject(api.platform, sessionEmail, p)}
                onRename={() => rename.open(p)}
                onDelete={e => void handleDelete(p.id, e)}
              />
            </div>
          )
        })}
      </div>
      {rename.target && <GccRenameModal {...rename} />}
    </div>
  )
}
